// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useLocation } from '@reach/router';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import SiteSearch from '../organisms/site-search';
import { useDynamicFooterDisclaimers } from '../../hooks/use-dynamic-footer-disclaimers';
import { useFooterLegal } from '../../hooks/use-footer-legal';
import ResponderLogoIcon from '../../images/first-responder-logo.svg';
import IconFacebook from '../../images/svg/facebook.svg';
import IconInstagram from '../../images/svg/instagram.svg';
import IconLinkedIn from '../../images/svg/linkedin.svg';
import IconTwitter from '../../images/svg/twitter.svg';
import RichTextEditorStyles from '../utils/RichTextEditorStyles';
import getPath from '../utils/paths';
import IconYoutube from '../../images/svg/youtube.svg';
import {
    SOCIAL_URL_FACEBOOK,
    SOCIAL_URL_LINKEDIN,
    SOCIAL_URL_TWITTER,
    SOCIAL_URL_YOUTUBE,
} from '../../constants/constants';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import FranchiseLicenses from '../molecules/franchise-licenses';
import getLink from '../utils/get-link';
import Container from '../atoms/Container';
import { useDniProvider, DniBrand } from '../../contexts/dni-provider';
import Link from '../primitives/link';
import { colors } from '../primitives/colors';

const staticLinks = [
    {
        title: 'Services',
        links: [
            {
                title: 'Water Damage',
                url: 'waterDamage',
            },
            {
                title: 'Fire Damage',
                url: 'fireDamage',
            },
            {
                title: 'Mold Remediation',
                url: 'moldRemediation',
            },
            {
                title: 'Storm/Disaster',
                url: 'stormDisaster',
            },
            {
                title: 'Construction',
                url: 'constructionService',
                sublinks: [
                    {
                        title: 'Residential Construction',
                        url: 'residentialConstruction',
                    },
                    {
                        title: 'Commercial Construction',
                        url: 'commercialConstruction',
                    },
                    {
                        title: 'Roof Tarp/Board-up',
                        url: 'roofTarpBoardUp',
                    },
                ],
            },
            {
                title: 'Commercial Services',
                url: 'commercialServices',
                sublinks: [
                    {
                        title: 'Large Loss',
                        url: 'largeLoss',
                    },
                    {
                        title: 'Emergency Ready Plan',
                        url: 'emergencyReadyPlan',
                    },
                    {
                        title: 'Industries Serviced',
                        url: 'industriesServiced',
                    },
                    {
                        title: 'Services',
                        url: 'services',
                    },
                ],
            },
            {
                title: 'Cleaning',
                url: 'cleaning',
                sublinks: [
                    {
                        title: 'Specialty Cleaning',
                        url: 'specialtyCleaningServices',
                        sublinks: [
                            {
                                title: 'Air Duct/HVAC Cleaning',
                                url: 'airDuctHVACCleaning',
                            },
                            {
                                title: 'Biohazard/Crime Scene',
                                url: 'biohazardCrimeScene',
                            },
                            {
                                title: 'Virus/Pathogen Cleaning',
                                url: 'covid19',
                            },
                            {
                                title: 'Document Restoration',
                                url: 'documentRestoration',
                            },
                            {
                                title: 'Odor Removal',
                                url: 'odorRemoval',
                            },
                            {
                                title: 'Vandalism/Graffiti',
                                url: 'vandalismGraffiti',
                            },
                        ],
                    },
                    {
                        title: 'General Cleaning',
                        url: 'generalCleaningServices',
                        sublinks: [
                            {
                                title: 'Carpet/Upholstery',
                                url: 'carpetUpholstery',
                            },
                            {
                                title: 'Ceiling/Floors/Walls',
                                url: 'ceilingFloorsWalls',
                            },
                            {
                                title: 'Drapes/Blinds',
                                url: 'drapesBlinds',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        title: 'About',
        links: [
            {
                title: 'Find a Location',
                url: 'findALocation',
            },
            {
                title: 'History',
                url: 'history',
            },
            {
                title: 'Insurance Professionals',
                url: 'insuranceProfessionals',
            },
            {
                title: 'Leadership',
                url: 'leadership',
            },
            {
                title: 'Sponsorships & Initiatives',
                url: '',
                sublinks: [
                    {
                        title: 'First Responder',
                        url: 'firstResponder',
                    },
                    {
                        title: 'PGA',
                        url: 'pga',
                    },
                ],
            },
            {
                title: 'Newsroom',
                url: '',
                sublinks: [
                    {
                        title: 'Media Resources',
                        url: 'mediaResources',
                    },
                    {
                        title: 'Press Releases',
                        url: 'pressReleases',
                    },
                    {
                        title: 'In the Community',
                        url: 'inTheCommunity',
                    },
                ],
            },
            {
                title: 'Advertising',
                url: 'advertising',
                other: '/resources/tv-commercials',
            },
            {
                title: 'Contact Us',
                url: 'contactUs',
            },
        ],
    },
    {
        title: 'Resources',
        links: [
            {
                title: 'Resources Home',
                url: 'resourcesHome',
                sublinks: [
                    {
                        title: 'Water',
                        url: 'water',
                    },
                    {
                        title: 'Fire',
                        url: 'fire',
                    },
                    {
                        title: 'Mold',
                        url: 'mold',
                    },
                    {
                        title: 'Storm',
                        url: 'storm',
                    },
                    {
                        title: 'Construction',
                        url: 'constructionResources',
                    },
                    {
                        title: 'Specialty Cleaning',
                        url: 'specialtyCleaningResources',
                    },
                    {
                        title: 'General Cleaning',
                        url: 'generalCleaningResources',
                    },
                    {
                        title: 'Why SERVPRO?',
                        url: 'whySERVPRO',
                    },
                ],
            },
            {
                title: 'FAQ',
                url: 'faq',
            },
            {
                title: 'Glossary',
                url: 'glossary',
            },
        ],
    },
    {
        title: 'Careers',
        links: [
            {
                title: 'Careers Home',
                url: 'careersHome',
                sublinks: [
                    {
                        title: 'Headquarters Careers',
                        url: 'headquarters',
                    },
                    {
                        title: 'Local Franchise Careers',
                        url: 'localFranchiseCareers',
                    },
                    {
                        title: 'Own a Franchise',
                        url: 'ownAFranchise',
                    },
                ],
            },
        ],
    },
    {
        title: 'Legal',
        links: [
            {
                title: 'Ingredients/Safety Data',
                url: 'ingredientsSafetyData',
            },
            {
                title: 'Terms & Conditions',
                url: 'termsConditions',
            },
            {
                title: 'Privacy Policy',
                url: 'privacyPolicy',
            },
            {
                title: 'Cookie Settings',
                className: 'ot-sdk-show-settings',
            },
            // {
            //   title: 'California: Your Privacy Rights',
            //   url: 'californiaPrivacyRights'
            // },
            // // TODO: Add link for 'Do Not Sell My Personal Information'
            // {
            //   title: 'Do Not Sell My Personal Information',
            //   url: 'doNotSellMyPersonalInformation'
            // }
        ],
    },
];

const Footer = (): JSX.Element => {

    const location = useLocation();

    const {
        isMicrosite,
        permalink: micrositePermalink,
        footer,
        franchise,
        ...microsite
    } = useMicrosite();

    const { provider: DniProvider } = useDniProvider();
    const servproUrlRegex = /^(http(s?):\/\/)?(www\.)?servpro\.com\/?$/;
    const { copy: staticDisclaimerCopy } = useFooterLegal();
    const { edges: dynamicFooterDisclaimers } = useDynamicFooterDisclaimers();

    const franchiseLinks = footer?.links;
    const links = isMicrosite ? franchiseLinks : staticLinks;

    return (
        <div>
            {isMicrosite && (
                <div css={styles.subfooterSocialLinksMicrosite}>
                    {franchise?.socialMedia?.linkedIn && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://linkedin.com/${franchise?.socialMedia?.linkedIn}`} target="_blank">
                            <IconLinkedIn />
                        </Link>
                    )}
                    {franchise?.socialMedia?.twitter && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://twitter.com/${franchise?.socialMedia?.twitter}`} target="_blank">
                            <IconTwitter />
                        </Link>
                    )}
                    {franchise?.socialMedia?.instagram && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://instagram.com/${franchise?.socialMedia?.instagram}`} target="_blank">
                            <IconInstagram />
                        </Link>
                    )}
                    {franchise?.socialMedia?.facebook && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://facebook.com/${franchise?.socialMedia?.facebook}`} target="_blank">
                            <IconFacebook />
                        </Link>
                    )}
                </div>
            )}
            {!isMicrosite && <div css={styles.autoPilot} className="be-ix-link-block" />}
            <footer css={styles.siteFooter(isMicrosite)}>
                <Container>
                    <section css={styles.widgetSection} id="main-footer--nav">
                        {!isMicrosite && (
                            <>
                                <p css={styles.footerMobileInfo}>
                                    801 Industrial Blvd
                                    <br />
                                    Gallatin, TN 37066
                                    <br />
                                </p>
                                <div css={styles.searchbarWrapper}>
                                    <SiteSearch />
                                </div>
                            </>
                        )}
                        <div css={styles.linksColumns(isMicrosite)} >
                            {links?.map((group, id) => {
                                if (!group) return null;
                                const doubleWidth = !isMicrosite && id === 0;
                                const lastItem = !isMicrosite && id === links.length - 1;
                                const penultItem = !isMicrosite && id === links.length - 2;

                                // Only do it for this level, this level is for the 'headings'
                                if (!group.links || group.links.filter(x => !!x).length === 0) return null;

                                return (
                                    <div css={styles.linksColumn(penultItem, lastItem)} key={id}>
                                        <p css={styles.navTitle}>{group.title}</p>
                                        <div css={styles.navContainer}>
                                            <ul css={styles.navLinks(doubleWidth)}>
                                                {group.links.filter(link => DniProvider !== DniBrand.TELMETRICS || (DniProvider === DniBrand.TELMETRICS && !servproUrlRegex.test(link?.url || ''))).map(
                                                    (groupLink, i) => {
                                                        if (!groupLink) return null;

                                                        const { title, url, sublinks = [] } = groupLink;
                                                        const className = groupLink.className || groupLink.class_name;

                                                        return (
                                                            <React.Fragment key={i}>
                                                                <li css={styles.navLinksItem}>
                                                                    {url ? (
                                                                        <Link
                                                                            to={getPath[url] || url}
                                                                            className={className}
                                                                        >
                                                                            {title}
                                                                        </Link>
                                                                    ) : (
                                                                        <p css={styles.cursorDefault} className={className}>
                                                                            {title}
                                                                        </p>
                                                                    )}
                                                                </li>
                                                                {sublinks.length > 0
                                                                    && sublinks.map(
                                                                        (sublink, j) => {
                                                                            const {
                                                                                title: sublinkTitle,
                                                                                url: sublinkUrl,
                                                                                sublinks: nestedSublinks = [],
                                                                            } = sublink;

                                                                            const subClassName = sublink.className || sublink.class_name;

                                                                            return (
                                                                                <React.Fragment key={j}>
                                                                                    <li css={[styles.navLinksItem, { "@media (min-width: 375px)": { marginLeft: "0.75rem" } }]}>
                                                                                        {sublinkUrl ? (
                                                                                            <Link
                                                                                                to={getPath[sublinkUrl] ? getPath[sublinkUrl] : sublinkUrl}
                                                                                                className={subClassName}
                                                                                            >
                                                                                                {sublinkTitle}
                                                                                            </Link>
                                                                                        ) : (
                                                                                            <p
                                                                                                css={styles.cursorDefault}
                                                                                                className={subClassName}
                                                                                            >
                                                                                                {sublinkTitle}
                                                                                            </p>
                                                                                        )}
                                                                                    </li>
                                                                                    {nestedSublinks.length > 0
                                                                                        && nestedSublinks.map(
                                                                                            (nestedSubLink, k) => {
                                                                                                const {
                                                                                                    title: nestedSublinkTitle,
                                                                                                    url: nestedSublinkUrl,
                                                                                                } = nestedSubLink;

                                                                                                const nestedClassName = nestedSubLink.className || nestedSubLink.class_name;

                                                                                                return (
                                                                                                    <li
                                                                                                        css={[styles.navLinksItem, { "@media (min-width: 375px)": { marginLeft: "0.5rem" } }]}
                                                                                                        key={k}
                                                                                                    >
                                                                                                        {nestedSublinkUrl ? (
                                                                                                            <Link
                                                                                                                to={getLink(getPath[
                                                                                                                    nestedSublinkUrl
                                                                                                                ] ? getPath[
                                                                                                                nestedSublinkUrl
                                                                                                                ] : nestedSublinkUrl, isMicrosite && micrositePermalink)}
                                                                                                                className={
                                                                                                                    nestedClassName
                                                                                                                }
                                                                                                            >
                                                                                                                {nestedSublinkTitle}
                                                                                                            </Link>
                                                                                                        ) : (
                                                                                                            <p
                                                                                                                css={styles.cursorDefault}
                                                                                                                className={
                                                                                                                    nestedClassName
                                                                                                                }
                                                                                                            >
                                                                                                                {nestedSublinkTitle}
                                                                                                            </p>
                                                                                                        )}
                                                                                                    </li>
                                                                                                );
                                                                                            },
                                                                                        )}
                                                                                </React.Fragment>
                                                                            );
                                                                        },
                                                                    )}
                                                            </React.Fragment>
                                                        );
                                                    },
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                );
                            })}
                            {isMicrosite && (
                                <div css={styles.linksColumn()}>
                                    <FranchiseLicenses />
                                </div>
                            )}
                            {isMicrosite && (
                                <address css={[
                                    styles.linksColumn(),
                                    {
                                        fontStyle: "normal",
                                        display: "block",
                                    }
                                ]}>
                                    {!franchise?.addressHidden && (
                                        <>
                                            <strong>{franchise?.name}</strong>
                                            <br />
                                            {!!franchise?.address.address && (
                                                <>
                                                    {franchise.address.address}
                                                    <br />
                                                </>
                                            )}
                                            {!!franchise?.address.addressExt && (
                                                <>
                                                    {franchise.address.addressExt}
                                                    <br />
                                                </>
                                            )}
                                            {franchise?.address?.city}
                                            {', '}
                                            {franchise?.address?.state}
                                            {' '}
                                            {franchise?.address?.postalCode}
                                        </>
                                    )}
                                </address>
                            )}
                        </div>
                        <div css={styles.navImageOuterWrap}>
                            <div css={styles.navImageInnerWrap} >
                                <StaticImage
                                    width={300}
                                    alt="Certificates"
                                    formats={['AUTO', 'WEBP']}
                                    src="../../images/certificates.png"
                                />
                                <ResponderLogoIcon />
                            </div>
                        </div>
                    </section>

                    {!!staticDisclaimerCopy && (
                        <section css={styles.dynamicDisclaimersWrap}>
                            <div
                                css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                dangerouslySetInnerHTML={{ __html: staticDisclaimerCopy }}
                            />
                        </section>
                    )}
                    <section css={styles.dynamicDisclaimersWrap}>
                        {dynamicFooterDisclaimers
                            .filter(({ node: fd }) => fd?.page_match === location?.pathname)
                            .map(({ node: fd }, idx) => (
                                <React.Fragment key={idx}>
                                    {!!fd?.content_before && (
                                        <div
                                            css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                            dangerouslySetInnerHTML={{ __html: fd.content_before }}
                                        />
                                    )}
                                    {!!fd?.content && (
                                        <div
                                            css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                            dangerouslySetInnerHTML={{ __html: fd.content }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                    </section>
                    <section css={styles.subfooter} id="main-footer--subfooter">
                        <div css={styles.subfooterCol}>
                            <p css={styles.colophon}>
                                ©
                                {' '}
                                {new Date().getFullYear()}
                                {' '}
                                SERVPRO. All rights reserved.
                            </p>
                        </div>
                        {!isMicrosite && (
                            <div css={styles.subfooterCol}>
                                <ul css={styles.subfooterSocialLinks}>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_LINKEDIN} target="_blank">
                                        <IconLinkedIn />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_TWITTER} target="_blank">
                                        <IconTwitter />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_YOUTUBE} target="_blank">
                                        <IconYoutube />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_FACEBOOK} target="_blank">
                                        <IconFacebook />
                                    </Link>
                                </ul>
                            </div>
                        )}
                    </section>
                </Container>
            </footer>
        </div>
    );
};

const styles = {
    cursorDefault: {
        cursor: "default"
    },
    autoPilot: {
        '&.be-ix-link-block': {
            backgroundColor: colors.primaryOrange,
            display: 'flex',
            justifyContent: "end",
            color: colors.shadesWhite,
            '.be-related-link-container': {
                padding: '1.5rem 3rem',
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: "column",
                textAlign: 'right',
                "@media (min-width: 768px)": {
                    padding: '1.5rem 2rem',
                    gap: '.35rem 1.5rem',
                    flexDirection: "row",
                },
                "@media (min-width: 1024px)": {
                    padding: '1rem 3rem',
                },
                '.be-label': {
                    fontWeight: '600',
                    flexBasis: 'auto',
                    marginBottom: '.25rem',
                    "@media (min-width: 768px)": {
                        gap: '1rem',
                        marginBottom: 0,
                    },
                },
                '.be-list': {
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: 'wrap',
                    "@media (min-width: 768px)": {
                        gap: '0 1rem',
                        flexDirection: "row",
                    },
                },
            },
        },
    },
    siteFooter: (isMicrosite) => ({
        position: "relative",
        zIndex: 10,
        backgroundColor: colors.shadesWhite,
        borderTopWidth: "2px",
        borderColor: colors.primaryOrange,
        marginBottom: isMicrosite ? "2.25rem" : "3.75rem",
        "@media (min-width: 375px)": {
            marginBottom: isMicrosite ? "3.75rem" : "5.75rem",
        },
        "@media (min-width: 1024px)": {
            marginBottom: 0,
        }
    }),
    footerMobileInfo: {
        maxWidth: "250px",
        marginBottom: "2rem",
        "@media (min-width: 1024px)": {
            display: "none"
        }
    },
    widgetSection: {
        position: "relative",
        paddingTop: "2.25rem",
        "@media (min-width: 1024px)": {
            paddingBottom: "1.75rem",
            marginBottom: "2rem"
        }
    },
    staticDisclaimer: {
        marginBottom: "1.5rem",
        lineHeight: 1.375,
    },
    dynamicDisclaimersWrap: {
        fontSize: "0.875rem",
        lineHeight: "1.25rem"
    },
    subfooter: {
        paddingTop: "0.75rem",
        paddingBottom: "0.75rem",
        justifyContent: "center",
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        "@media (min-width: 1024px)": {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopWidth: "1px",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            borderColor: colors.grey300,
        }
    },
    subfooterCol: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        "@media (min-width: 1024px)": {
            flex: "1 1 auto"
        }
    },
    subfooterSocialLinks: {
        display: "flex",
        paddingBottom: "0.75rem",
        paddingTop: "1rem",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
        "@media (min-width: 1024px)": {
            marginLeft: "auto",
            maxWidth: "max-content",
        },
        ">:not([hidden])~:not([hidden])": {
            marginLeft: "3rem",
        }
    },
    subfooterSocialLink: {
        color: colors.primaryOrange,
        svg: {
            fill: colors.primaryOrange,
        }
    },
    subfooterSocialLinksMicrosite: {
        display: "flex",
        paddingTop: "0.75rem",
        paddingBottom: "0.75rem",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.primaryOrange,
        ">:not([hidden])~:not([hidden])": {
            marginLeft: '2rem'
        }
    },
    subfooterSocialLinkLight: {
        color: colors.shadesWhite,
        svg: {
            fill: colors.shadesWhite,
        }
    },
    colophon: {
        width: "100%",
        textAlign: "center",
        "@media (min-width: 1024px)": {
            paddingTop: "1rem",
            paddingBottom: "1rem",
            textAlign: "left",
            width: "auto"
        }
    },
    navImageOuterWrap: {
        display: "flex",
        right: "0",
        bottom: "0",
        left: "0",
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem"
    },
    navImageInnerWrap: {
        display: "flex",
        paddingBottom: "0.5rem",
        marginTop: "0.5rem",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        "@media (min-width: 640px)": {
            paddingBottom: "0",
            marginTop: "0",
            flexDirection: "row",
            ">:not([hidden])~:not([hidden])": {
                marginLeft: "2rem",
            }
        },
        "@media (min-width: 1024px)": {
            justifyContent: "flex-end"
        }
    },
    searchbarWrapper: {
        maxWidth: "325px",
        marginBottom: "3rem",
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        "@media (min-width: 1024px)": {
            display: "none",
            paddingRight: "0"
        }
    },
    linksColumns: (isMicrosite: boolean) => ({
        display: "grid",
        gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
        alignItems: "flex-start",
        gridAutoFlow: "dense",
        "@media (min-width: 768px)": {
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between"
        },
        "@media (min-width: 1024px)": {
            gridTemplateColumns: isMicrosite ? '1fr 1fr 1fr 1fr' : '2fr 1fr 1fr 1fr',
            gridTemplateRows: "1fr 2fr",
        },
    }),
    linksColumn: (penultItem?: boolean, lastItem?: boolean) => ({
        marginBottom: "1.25rem",
        "@media (min-width: 1024px)": {
            marginBottom: "0",
            gridRow: lastItem ? "2 / 3" : penultItem ? "1 / 2" : "1 / 3",
            gridColumn: (lastItem || penultItem) && "4 / 5",
        },
    }),
    navTitle: {
        paddingBottom: "0.25rem",
        marginBottom: "0.5rem",
        width: "10rem",
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        fontWeight: 500,
        color: colors.primaryOrange,
        "@media (min-width: 1024px)": {
            borderBottomWidth: "1px",
            borderColor: colors.primaryOrange,
            fontSize: "21px"
        }
    },
    navContainer: {
        display: "flex",
        "@media (min-width: 1024px)": {
            flexDirection: "row"
        }
    },
    navLinks: (doubleWidth) => ({
        display: "flex",
        flexDirection: "column",
        gap: !doubleWidth && "0",
        marginRight: "0.75rem",
        marginBottom: "1rem",
        gridAutoFlow: "column",
        gridTemplateRows: "repeat(15, min-content)",
        "@media (min-width: 375px)": {
            width: "100%",
            marginBottom: "0"
        },
        "@media (min-width: 1024px)": {
            display: "grid",
            gridTemplateColumns: doubleWidth ? "repeat(2, minmax(0, 1fr))" : "repeat(1, minmax(0, 1fr))",
        }
    }),
    navLinksItem: {
        marginBottom: "0.75rem",
        '.nav-links': {
            background: "red",
            paddingLeft: "0.75rem"
        }
    }
};

export default Footer;
